/**
 * Tagada Rx secondary nav — same visual language as store UnifiedNavigation
 * sub-tabs (primary-tinted pills), not a custom emerald chip bar.
 *
 * Hidden until the integration is active so merchants aren't dumped into
 * empty Cases / Products screens pre-activation.
 */

import { useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Stethoscope, ScrollText, Settings2, Receipt, Package, Globe } from 'lucide-react';

import { useAccountManager } from '@/hooks/use-account-manager';
import { useStoreNavigate } from '@/hooks/use-app-navigate';
import { useRxIntegrationStatus } from '@/lib/api/rx';
import { cn } from '@/lib/utils';

const ITEMS = [
  { key: 'overview', labelKey: 'nav.overview', icon: Stethoscope, route: 'RX_OVERVIEW' as const },
  { key: 'cases', labelKey: 'nav.cases', icon: ScrollText, route: 'RX_CASES' as const },
  { key: 'products', labelKey: 'nav.products', icon: Package, route: 'RX_PRODUCT_OFFERINGS' as const },
  { key: 'storefront', labelKey: 'nav.storefront', icon: Globe, route: 'RX_STOREFRONT' as const },
  { key: 'statement', labelKey: 'nav.statement', icon: Receipt, route: 'RX_STATEMENT' as const },
  // Clinical routing and platform credentials: nothing a brand can change, staff only.
  { key: 'settings', labelKey: 'nav.settings', icon: Settings2, route: 'RX_CLINICAL_CONFIG' as const, staffOnly: true },
];

export function RxNav() {
  const { t } = useTranslation('rx');
  const { id: storeId, storeRoutes } = useStoreNavigate();
  const location = useLocation();
  const statusQuery = useRxIntegrationStatus(storeId);
  const { isAccountManager } = useAccountManager();

  if (statusQuery.data?.state !== 'active') {
    return null;
  }

  return (
    <nav className="bg-card inline-flex max-w-full flex-wrap items-center gap-1 rounded-lg border p-1 shadow-sm">
      {ITEMS.filter((item) => !('staffOnly' in item && item.staffOnly) || isAccountManager).map((item) => {
        const href = storeRoutes[item.route]();
        const overviewHref = storeRoutes.RX_OVERVIEW();
        const active =
          item.key === 'overview'
            ? location.pathname === overviewHref
            : location.pathname === href || location.pathname.startsWith(`${href}/`);
        const Icon = item.icon;
        return (
          <Link
            key={item.key}
            to={href}
            className={cn(
              'flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md border px-3 text-xs font-semibold transition-all',
              active
                ? 'bg-primary text-primary-foreground border-primary/50 shadow-md'
                : 'text-muted-foreground border-transparent hover:bg-primary/10 hover:text-primary hover:border-primary/30',
            )}
          >
            <Icon className="h-3.5 w-3.5" />
            {t(item.labelKey)}
          </Link>
        );
      })}
    </nav>
  );
}
